import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@web-chat-core/ui-kit';
import { selectRoom, selectRoomMetadata } from 'state/selectors';
import { roomInfoStore } from 'state/store';
import { useConnectToRoom } from 'features/PreJoin/hooks/useConnectToRoom';
import { getConferenceJwtToken } from 'features/AccessRequests/utils/getConferenceJwtToken';
import { createLogger } from '@web-chat-core/toolkit';
import { changeWebinarModeApi } from 'useCase/webinar/webinarApi';
import { getWebinarMode } from 'useCase/webinar/webinarStatus';
import { skipWebinarSpeakerLayoutForLocalEnabler } from 'useCase/webinar/useWebinarAutoLayout';
import { skipWebinarModeNotificationForLocalInitiator } from 'useCase/webinar/useWebinarModeNotifications';
import { publishBroadcastCommand } from 'useCase/moderation/commands/publishCommand';
import { MODERATOR_ACTIONS } from 'useCase/moderation/constants';
import type { WebinarModeData } from 'api/types';

const logger = createLogger({ prefix: 'Webinar' });

export const useApplyWebinarMode = () => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { getTokenAndWssUrl } = useConnectToRoom();

    const applyWebinarMode = useCallback(
        async (update: Partial<WebinarModeData>) => {
            const currentMetadata = selectRoomMetadata();
            if (!currentMetadata) return;

            const roomAtToggle = selectRoom();
            const prevWebinarMode = getWebinarMode() ?? {};
            const nextWebinarMode: WebinarModeData = { ...prevWebinarMode, ...update };
            const enabledChanged = update.enabled !== undefined && update.enabled !== prevWebinarMode.enabled;

            // Кто включил вебинар не переключаем ему layout на SPEAKER
            if (update.enabled === true && !prevWebinarMode.enabled) {
                skipWebinarSpeakerLayoutForLocalEnabler();
            }

            if (enabledChanged) {
                skipWebinarModeNotificationForLocalInitiator();
            }

            roomInfoStore.setWebinarMode(nextWebinarMode);

            const jwtToken = await getConferenceJwtToken(getTokenAndWssUrl);
            const success = await changeWebinarModeApi({ webinarMode: nextWebinarMode, jwtToken });

            if (!success) {
                if (enabledChanged) {
                    skipWebinarModeNotificationForLocalInitiator();
                }
                roomInfoStore.setWebinarMode(prevWebinarMode);
                showSnackbar(t('web_videoconference_error'));
                return;
            }

            const room = selectRoom() ?? roomAtToggle;

            if (enabledChanged) {
                const action = update.enabled ? MODERATOR_ACTIONS.START_WEBINAR : MODERATOR_ACTIONS.END_WEBINAR;
                const published = await publishBroadcastCommand(room, action);
                if (!published) {
                    logger.warn('START/END_WEBINAR не отправлен на mobile', { action, hasRoom: Boolean(room) });
                }
            }

            // При включении блокировки микрофона, принудительно выключаем микрофоны у не модераторов
            if (update.microDisabled === true && !prevWebinarMode.microDisabled) {
                await publishBroadcastCommand(room, MODERATOR_ACTIONS.WEBINAR_FORCE_MIC_OFF);
            }

            // При включении блокировки камеры, принудительно выключаем камеры у не модераторов
            if (update.cameraDisabled === true && !prevWebinarMode.cameraDisabled) {
                await publishBroadcastCommand(room, MODERATOR_ACTIONS.WEBINAR_FORCE_CAM_OFF);
            }

            // При включении блокировки шаринга экрана, принудительно останавливаем демонстрацию у слушателей
            if (update.screenShareDisabled === true && !prevWebinarMode.screenShareDisabled) {
                await publishBroadcastCommand(room, MODERATOR_ACTIONS.WEBINAR_FORCE_SCREEN_SHARE_OFF);
            }

            // При первичном включении вебинара, применяем все активные блокировки
            if (update.enabled === true) {
                if (nextWebinarMode.microDisabled) {
                    await publishBroadcastCommand(room, MODERATOR_ACTIONS.WEBINAR_FORCE_MIC_OFF);
                }
                if (nextWebinarMode.cameraDisabled) {
                    await publishBroadcastCommand(room, MODERATOR_ACTIONS.WEBINAR_FORCE_CAM_OFF);
                }
                if (nextWebinarMode.screenShareDisabled) {
                    await publishBroadcastCommand(room, MODERATOR_ACTIONS.WEBINAR_FORCE_SCREEN_SHARE_OFF);
                }
            }
        },
        [getTokenAndWssUrl, showSnackbar, t],
    );

    return { applyWebinarMode };
};
